import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStateScope } from '../context/StateScopeContext';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Send, User, Loader2, Sparkles, History, MessageSquare, Clock, ChevronRight, Users, Check, ArrowLeft, Plus, Search, Phone, Lock, Wifi, WifiOff, BellOff, Bell, EyeOff, Eye, X as XIcon, SlidersHorizontal } from 'lucide-react';
import notificationService from '../services/NotificationService';
import { usePageHeader } from '../context/PageHeaderContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import PageLoader from '../components/PageLoader';

// AI Character configurations
// ----- Swipeable conversation row (Mute / Hide drawer) -----
function SwipeableConvRow({ id, isMuted, hidden, offset, setOffset, onMute, onHide, onClick, children, palette = 'green' }) {
  const startXRef = React.useRef(null);
  const movedRef = React.useRef(false);
  const containerRef = React.useRef(null);
  const ACTION_W = 144; // px — width of the Mute+Hide drawer (72 each)

  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
    movedRef.current = false;
  };
  const handleTouchMove = (e) => {
    if (startXRef.current === null) return;
    const dx = e.touches[0].clientX - startXRef.current;
    if (Math.abs(dx) > 5) movedRef.current = true;
    // Allow leftward (negative dx) drag up to ACTION_W; allow right drag to close.
    let newOffset = (offset || 0) + dx;
    if (newOffset > 0) newOffset = 0;
    if (newOffset < -ACTION_W - 30) newOffset = -ACTION_W - 30;
    setOffset(id, newOffset);
  };
  const handleTouchEnd = () => {
    if (!movedRef.current) {
      startXRef.current = null;
      return;
    }
    // Snap open or closed depending on how far we are.
    const cur = offset || 0;
    if (cur < -ACTION_W / 2) setOffset(id, -ACTION_W);
    else setOffset(id, 0);
    startXRef.current = null;
    setTimeout(() => { movedRef.current = false; }, 50);
  };

  const handleClick = (e) => {
    if (movedRef.current) { e.preventDefault(); e.stopPropagation(); return; }
    if ((offset || 0) < 0) {
      // First tap closes the drawer.
      setOffset(id, 0);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) onClick();
  };

  const muteBg = palette === 'gold' ? 'bg-[#a39572]' : 'bg-[#9aa893]';
  return (
    <div className={`relative overflow-hidden rounded-2xl ${hidden ? 'opacity-60' : ''}`} ref={containerRef}>
      {/* iter125 — Drawer only renders when card is actually slid.
          Prevents the drawer colors from peeking through the rounded
          corners as a colored sliver at rest. */}
      {(offset || 0) < -0.5 && (
        <div className="absolute inset-y-0 right-0 flex items-stretch rounded-r-2xl overflow-hidden" style={{ width: 144 }}>
          <button
            className={`flex-1 ${muteBg} text-white text-xs font-semibold flex flex-col items-center justify-center gap-1`}
            onClick={(e) => { e.stopPropagation(); onMute(); }}
            data-testid={`mute-btn-${id}`}
          >
            {isMuted ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
            <span>{isMuted ? 'Unmute' : 'Mute'}</span>
          </button>
          <button
            className="flex-1 bg-gray-700 text-white text-xs font-semibold flex flex-col items-center justify-center gap-1"
            onClick={(e) => { e.stopPropagation(); onHide(); }}
            data-testid={`hide-btn-${id}`}
          >
            <EyeOff className="w-5 h-5" />
            <span>{hidden ? 'Unhide' : 'Hide'}</span>
          </button>
        </div>
      )}
      {/* iter125 — Card sized to match EmailPage thread cards exactly:
          rounded-2xl, px-3 py-3, same shadow + border treatment. */}
      <div
        className="bg-white dark:bg-[#141414] rounded-2xl px-3 py-3 cursor-pointer hover:shadow-md transition-transform border border-black/10 dark:border-white/10 shadow-sm relative"
        style={{ transform: `translateX(${offset || 0}px)`, transition: startXRef.current === null ? 'transform 200ms ease' : 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
        data-testid={`conv-row-${id}`}
      >
        {children}
        {/* iter125 — Desktop reveal-trigger chevron in the bottom-left
            corner — bare arrow, no background. Matches EmailPage. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            const cur = offset || 0;
            setOffset(id, cur === 0 ? -ACTION_W : 0);
          }}
          className="hidden md:flex absolute bottom-1.5 left-1.5 text-[#586D52]/70 hover:text-[#586D52] items-center justify-center"
          aria-label="Show actions"
          data-testid={`conv-row-${id}-chevron`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// AI Character configurations
const AI_CHARACTERS = {
  orb: {
    id: 'orb',
    name: 'ORB Ai Assistant',
    shortDesc: 'Friendly, clear, and action-oriented',
    longDesc: 'A friendly, mission‑driven guide that keeps things clear and conversational, helps users collaborate and make decisions quickly, and always nudges toward practical next steps and real‑world action.',
    image: null,
    color: '#586D52'
  },
  mena: {
    id: 'mena',
    name: 'Mena ORB',
    shortDesc: 'Strategic, ethical, and future‑focused',
    longDesc: 'A calm, principled guide who speaks with strategic clarity—helping users think ethically, long‑term, and with purpose.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/67lqvc7a_Mena%20Standard.png',
    color: '#8B5CF6'
  },
  katie: {
    id: 'katie',
    name: 'Katie ORB',
    shortDesc: 'Warm, steady, and deeply supportive',
    longDesc: 'A steady, nurturing presence who responds with warmth, reassurance, and practical wisdom—making users feel supported and grounded.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/b90ayuat_katie%20standard.png',
    color: '#EC4899'
  },
  luke: {
    id: 'luke',
    name: 'Luke ORB',
    shortDesc: 'Quiet, precise, and trust‑driven',
    longDesc: 'A reserved, trust‑first operator who communicates clearly and minimally, prioritizing safety, verification, and reliability.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/ue5pv5dv_Luke%20Standard.png',
    color: '#3B82F6'
  },
  dorian: {
    id: 'dorian',
    name: 'Dorian ORB',
    shortDesc: 'Direct, resilient, and action‑oriented',
    longDesc: 'A no‑nonsense, action‑oriented voice that cuts through noise and focuses users on resilience, realism, and next steps.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/bi6kcptf_Dorian%20Standard.png',
    color: '#F59E0B'
  },
  octavio: {
    id: 'octavio',
    name: 'Octavio ORB',
    shortDesc: 'Creative, expressive, and energizing',
    longDesc: 'An expressive, high‑energy conversationalist who engages through creativity, culture, and emotional momentum.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/zi3ibx7q_Octavio%20Standard.png',
    color: '#10B981'
  }
};

const STORAGE_KEY = 'orb_selected_character';
const SESSION_KEY = 'orb_chat_session_id';
const CHAT_STATE_KEY = 'orb_chat_state';

// CharacterAvatar - hoisted out of ChatPage so the <img> element is stable across
// re-renders and doesn't reload (which causes a "blinking" effect).
const CharacterAvatar = React.memo(function CharacterAvatar({ character, size = 'md', fullHeight = false, clickable = false, onNavigate }) {
  const sizeClasses = { sm: 'w-7 h-9', md: 'w-10 h-14', lg: 'w-16 h-22' };

  const handleClick = () => {
    if (clickable && character?.id && onNavigate) onNavigate(character.id);
  };

  if (!character || character.id === 'orb' || !character.image) {
    const charColor = character?.color || '#586D52';
    return (
      <div
        className={`${size === 'sm' ? 'w-7 h-7' : size === 'md' ? 'w-10 h-10' : 'w-16 h-16'} rounded-xl flex items-center justify-center flex-shrink-0 ${clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
        style={{ backgroundColor: `${charColor}20` }}
        onClick={handleClick}
      >
        <Sparkles className={size === 'lg' ? 'w-8 h-8' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4'} style={{ color: charColor }} />
      </div>
    );
  }

  if (fullHeight) {
    return (
      <div
        className={`w-14 rounded-xl overflow-hidden border-2 flex-shrink-0 self-center ${clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
        style={{ borderColor: character.color }}
        onClick={handleClick}
      >
        <img src={character.image} alt={character.name} className="w-full h-full object-cover object-top" style={{ aspectRatio: '3/4' }} />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClasses[size]} rounded-xl overflow-hidden border-2 flex-shrink-0 ${clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      style={{ borderColor: character.color }}
      onClick={handleClick}
    >
      <img src={character.image} alt={character.name} className="w-full h-full object-cover object-top" />
    </div>
  );
});

const getMessageCharacter = (message) => {
  const charId = message.character || 'orb';
  return AI_CHARACTERS[charId] || AI_CHARACTERS['orb'];
};

const ChatPage = () => {
  const { api, user, isFeatureEnabled } = useAuth();
  const { isRestricted } = useStateScope();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Override the mobile header — title becomes "Chats" (default
  // notification + menu icons remain on the right).
  usePageHeader({ title: 'Chats' }, []);

  // Main tab: 'internal', 'ai', 'external'
  // If the URL has ?conv=<id> (deep link from a notification), default to
  // internal so the team conversations are loaded immediately.
  // Restricted-state users have no AI tab — default them to internal.
  // iter175cl — ORB Text deep-link: `/chat?compose=external&prefill=<text>`
  // routes the user straight to the New External Text picker with the
  // message body already queued. Once they pick a recipient and tap
  // Start Conversation, the compose box auto-fills with the prefill,
  // so all they have to do is tap Send.
  const initialComposeExternal = searchParams.get('compose') === 'external';
  const initialPrefill = searchParams.get('prefill') || '';
  const pendingExternalPrefillRef = useRef(initialPrefill);

  const initialMainTab = searchParams.get('tab')
    || (initialComposeExternal ? 'external'
    : (searchParams.get('conv') ? 'internal' : (isRestricted ? 'internal' : 'ai')));
  const [mainTab, setMainTab] = useState(initialMainTab);

  // If a restricted-state user somehow lands on the AI tab (e.g. via
  // a deep link, ?tab=ai, or cached state), bounce them to internal.
  useEffect(() => {
    if (isRestricted && mainTab === 'ai') setMainTab('internal');
  }, [isRestricted, mainTab]);

  // iter175cl — Strip `compose` and `prefill` query params from the
  // URL once we've consumed them so a refresh doesn't re-trigger the
  // composer. The ref still holds the prefill for the eventual
  // conversation-view mount.
  useEffect(() => {
    if (initialComposeExternal || initialPrefill) {
      const next = new URLSearchParams(searchParams);
      next.delete('compose');
      next.delete('prefill');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
  // AI Chat state
  const [messages, setMessages] = useState(() => {
    // Restore messages from sessionStorage if available
    const saved = sessionStorage.getItem(CHAT_STATE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.messages || [];
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [conversations, setConversations] = useState([]);
  const [showCharacterPicker, setShowCharacterPicker] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [isViewingHistory, setIsViewingHistory] = useState(() => {
    const saved = sessionStorage.getItem(CHAT_STATE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.isViewingHistory || false;
      } catch (e) {
        return false;
      }
    }
    return false;
  });
  const [currentSessionId, setCurrentSessionId] = useState(() => {
    // Restore session ID from sessionStorage if available
    const saved = sessionStorage.getItem(CHAT_STATE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.sessionId || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const teamChatScrollRef = useRef(null);
  // Pull-to-refresh on the Team Chat list. Other tabs (DMs, AI chat)
  // are real-time via WebSocket and don't need a manual reload.
  const { pullY: ptrPullY, refreshing: ptrRefreshing } = usePullToRefresh({
    onRefresh: async () => {
      if (typeof loadConversations === 'function') await loadConversations();
    },
    scrollRef: teamChatScrollRef,
    enabled: true,
  });
  const messagesEndRef = useRef(null);

  // Music player input-focus handler (still used for visual feedback elsewhere)
  const { setIsInputFocused } = useMusicPlayer();

  // When the chat input is focused, drop the bottom-nav offset so the input rides
  // directly with the keyboard (iOS Safari auto-positions fixed elements above the keyboard)

  // Bottom of the chat panel:
  // • Idle: var(--app-bottom-h) (4rem on mobile + iPad portrait to clear
  //   the bottom nav; 0 on desktop landscape where the nav lives in the
  //   top header).
  // • Focused (mobile): 0 — Layout hides the nav and iOS Safari auto-
  //   positions the focused input above the keyboard.
  // • Focused (iPad portrait): the bottom nav stays put, AND we lift
  //   the input above the on-screen keyboard via --kb-offset so iOS
  //   doesn't scroll the entire page upward to reveal the focused
  //   field. var(--kb-offset) is 0 when no keyboard is open.
  const bottomOffset = inputFocused
    ? 'calc(var(--kb-offset, 0px) + 0px)'
    : 'var(--app-bottom-h, 4rem)';

  // Handle input focus for mini player transformation
  const handleChatInputFocus = () => {
    setInputFocused(true);
    if (setIsInputFocused) setIsInputFocused(true);
  };

  const handleChatInputBlur = () => {
    setInputFocused(false);
    if (setIsInputFocused) setIsInputFocused(false);
  };

  // Team Chat & External Text state
  const [internalUsers, setInternalUsers] = useState([]);
  const [externalContacts, setExternalContacts] = useState([]);
  const [teamConversations, setTeamConversations] = useState([]);
  const [externalConversations, setExternalConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [teamMessages, setTeamMessages] = useState([]);
  const [newTeamMessage, setNewTeamMessage] = useState('');
  const [teamView, setTeamView] = useState(initialComposeExternal ? 'new-conversation' : 'list'); // 'list', 'new-conversation', 'conversation'
  const [toSearchQuery, setToSearchQuery] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]); // group chat: list of teammates
  const [externalPhoneNumber, setExternalPhoneNumber] = useState('');
  const [teamLoading, setTeamLoading] = useState(true);
  // iter123 — In-row search box (replaces the static "Team
  // Conversations" / "External Texts" mini-header label). Filters
  // the list by participant name, last message, OR phone number
  // (external tab only).
  const [listSearch, setListSearch] = useState('');
  // iter125 — Filter pill flow mirrors the EmailPage filter row.
  // Funnel button swaps the search bar for Muted + Hidden toggles.
  const [chatFilterMode, setChatFilterMode] = useState('search'); // 'search' | 'filter'
  const [chatFilterMuted, setChatFilterMuted] = useState(false);
  const [chatFilterHidden, setChatFilterHidden] = useState(false);
  // Per-conversation prefs (mute / hide). Map keyed by conversation/contact id.
  const [convPrefs, setConvPrefs] = useState({});
  // UI: temporarily reveal hidden threads. Auto-rehide after 60s.
  const [showHidden, setShowHidden] = useState(false);
  const showHiddenTimerRef = useRef(null);
  // Per-row swipe offset (px). Negative = revealed action drawer.
  const [swipeOffsets, setSwipeOffsets] = useState({});
  
  // WebSocket state for real-time chat
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const reconnectTimeoutRef = useRef(null);

  const isOnboarded = user?.is_onboarded || user?.role === 'admin';

  const [selectedCharacter, setSelectedCharacter] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && AI_CHARACTERS[saved] ? saved : 'orb';
  });

  const currentCharacter = AI_CHARACTERS[selectedCharacter];

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Load Team Chat and External Text data
  useEffect(() => {
    if (isOnboarded && (mainTab === 'internal' || mainTab === 'external')) {
      loadTeamData();
    }
  }, [isOnboarded, mainTab]);

  const loadTeamData = async () => {
    setTeamLoading(true);
    try {
      const usersRes = await api.get('/users/all');
      setInternalUsers(usersRes.data.filter(u => u.id !== user?.id));
      
      const contactsRes = await api.get('/contacts');
      setExternalContacts(contactsRes.data.external || []);
      
      await loadTeamConversations();
      
      // Load Twilio external contacts for external text tab
      if (mainTab === 'external') {
        await loadTwilioExternalContacts();
      }
    } catch (error) {
      console.error('Failed to load team data:', error);
    } finally {
      setTeamLoading(false);
    }
  };

  const loadTwilioExternalContacts = async () => {
    // iter125.3 — Cross-reference the Twilio threads with the user's
    // own external_contacts so we can attach the saved title + photo
    // + company. The Twilio response only carries name + phone.
    let externalById = new Map();
    let externalByPhone = new Map();
    try {
      const r2 = await api.get('/contacts');
      for (const c of (r2.data?.external || [])) {
        if (c.id) externalById.set(c.id, c);
        if (c.phone) externalByPhone.set(String(c.phone).replace(/\D/g, ''), c);
      }
    } catch (_e) {/* silent */}
    try {
      const res = await api.get('/twilio/contacts/my-external');
      const twilioContacts = (res.data.contacts || []).map(c => {
        const phoneKey = String(c.phone_number || '').replace(/\D/g, '');
        const extra = externalById.get(c.id) || externalByPhone.get(phoneKey) || {};
        const company = extra.company || '';
        const title = extra.title || '';
        const subtitle = [company, title].filter(Boolean).join(', ') || c.phone_number;
        return {
          id: c.id,
          type: 'external',
          participant_name: c.name || c.phone_number,
          participant_title: subtitle,
          participant_picture: extra.profile_picture || '',
          phone_number: c.phone_number,
          last_message: c.last_message,
          last_message_at: c.last_message_at,
          message_count: c.message_count,
          _twilio: true,
        };
      });
      setExternalConversations(twilioContacts);
    } catch (error) {
      console.error('Failed to load Twilio contacts:', error);
      // Fallback to existing conversations
      try {
        const res = await api.get('/messages/conversations');
        const allConvos = res.data || [];
        const externals = allConvos.filter((c) => c.type === 'external').map((c) => {
          const ext = c.external_contact || {};
          const company = ext.company || '';
          const title = ext.title || '';
          const subtitle = [company, title].filter(Boolean).join(', ') || c.phone_number;
          return {
            ...c,
            participant_name: ext.name || c.phone_number || 'External',
            participant_title: subtitle,
            participant_picture: ext.profile_picture || '',
          };
        });
        setExternalConversations(externals);
      } catch (e) {
        setExternalConversations([]);
      }
    }
  };

  const loadTeamConversations = async () => {
    try {
      const res = await api.get('/messages/conversations');
      const allConvos = res.data || [];
      // iter125.3 — Normalize the API shape into the per-row fields
      // the cards consume (`participant_name`, `participant_title`,
      // `participant_picture`). The API returns nested `other_user`
      // for team chats and `external_contact` for external texts; we
      // flatten so the UI doesn't have to branch.
      const teamConvs = allConvos.filter((c) => c.type === 'team').map((c) => {
        const u = c.other_user || {};
        const fullName = u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim();
        return {
          ...c,
          participant_name: fullName || 'Team Member',
          participant_title: u.title || u.role || 'Team Member',
          participant_picture: u.profile_picture || '',
        };
      });
      setTeamConversations(teamConvs);
    } catch (error) {
      setTeamConversations([]);
    }
  };

  // WebSocket connection for real-time team chat
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Build WebSocket URL from backend URL - use /api/ws/chat for proper routing
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/chat/${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        
        // Subscribe to current conversation if one is selected
        if (selectedConversation) {
          wsRef.current.send(JSON.stringify({
            action: 'subscribe',
            conversation_id: selectedConversation.id
          }));
        }
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_message') {
            // Add new message to the chat if it's for the current conversation
            if (data.conversation_id === selectedConversation?.id) {
              setTeamMessages(prev => [...prev, {
                id: data.message.id,
                content: data.message.content,
                sender_id: data.message.sender_id,
                timestamp: data.message.timestamp,
                is_mine: data.message.sender_id === user?.id
              }]);
              setTimeout(scrollToTeamBottom, 100);
            }
            
            // Show native notification for messages from others when not focused
            if (data.message.sender_id !== user?.id && document.hidden) {
              const sender = { 
                name: data.message.sender_name || 'New Message',
                id: data.message.sender_id,
                profile_picture: data.message.sender_avatar
              };
              notificationService.showMessage(
                sender,
                data.message.content,
                data.conversation_id
              ).catch(err => console.log('[Notification] Could not show:', err));
            }
            
            // Update conversation list
            loadTeamConversations();
          } else if (data.type === 'message_sent') {
            // Message confirmation - update temp message with real id
            setTeamMessages(prev => {
              const tempIdx = prev.findIndex(m => m.id?.startsWith('temp-'));
              if (tempIdx !== -1) {
                const updated = [...prev];
                updated[tempIdx] = {
                  ...updated[tempIdx],
                  id: data.message.id
                };
                return updated;
              }
              return prev;
            });
          } else if (data.type === 'subscribed') {
            console.log('Subscribed to conversation:', data.conversation_id);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected - will use polling');
        setWsConnected(false);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setWsConnected(false);
    }
  }, [selectedConversation, user?.id, loadTeamConversations]);

  // Polling fallback for real-time updates when WebSocket fails
  const pollingIntervalRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const userRef = useRef(null);
  
  // Keep the refs in sync with state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return; // Already polling
    
    console.log('Starting polling for real-time updates');
    setIsPolling(true);
    pollingIntervalRef.current = setInterval(async () => {
      const currentConv = selectedConversationRef.current;
      if (currentConv && !currentConv._twilio && !String(currentConv.id || '').startsWith('__new_ext_')) {
        try {
          const res = await api.get(`/messages/conversations/${currentConv.id}`);
          // Backend returns flat List[MessageResponse]
          const raw = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
          const newMessages = raw.map((m) => ({
            id: m.id,
            content: m.content,
            sender_id: m.sender_id,
            timestamp: m.created_at || m.timestamp,
            is_mine: m.sender_id === userRef.current?.id,
          }));
          setTeamMessages((prev) => {
            // Replace only when there's MORE persisted than what we currently
            // show, OR when the persisted set differs in IDs from what we have.
            const persistedReal = prev.filter((m) => !String(m.id || '').startsWith('temp-'));
            if (newMessages.length > persistedReal.length) {
              setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
              // Keep any optimistic temps that haven't shown up yet.
              const tempStill = prev.filter((m) => String(m.id || '').startsWith('temp-'));
              return [...newMessages, ...tempStill];
            }
            return prev;
          });
        } catch (error) {
          console.error('Polling error:', error);
        }
      }
    }, 3000); // Poll every 3 seconds
  }, [api]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  }, []);

  // Connect WebSocket when on internal team tab
  useEffect(() => {
    if (mainTab === 'internal' && isOnboarded) {
      connectWebSocket();
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [mainTab, isOnboarded, connectWebSocket]);

  // Start polling as fallback when WebSocket is not connected
  useEffect(() => {
    let pollingTimeout = null;
    
    if (mainTab === 'internal' && selectedConversation && !wsConnected && isOnboarded) {
      // Give WebSocket a moment to connect, then start polling if still not connected
      pollingTimeout = setTimeout(() => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          startPolling();
        }
      }, 1500);
    } else if (mainTab !== 'internal' || !selectedConversation) {
      // Only stop polling when leaving the tab or deselecting conversation
      stopPolling();
    }
    
    return () => {
      if (pollingTimeout) clearTimeout(pollingTimeout);
    };
  }, [mainTab, selectedConversation, wsConnected, isOnboarded, startPolling, stopPolling]);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Subscribe to conversation when selected
  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation) {
      wsRef.current.send(JSON.stringify({
        action: 'subscribe',
        conversation_id: selectedConversation.id
      }));
    }
  }, [selectedConversation]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const getTeamInitials = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    }
    return 'U';
  };

  const scrollToTeamBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelectTeamConversation = async (conv) => {
    setSelectedConversation(conv);
    setTeamView('conversation');
    try {
      if (conv._twilio) {
        // Load Twilio message history
        const res = await api.get(`/twilio/contacts/${conv.id}/history`);
        const twilioMessages = (res.data.messages || []).map(m => ({
          id: m.id,
          content: m.body,
          sender_id: m.direction === 'outbound' ? user?.id : 'external',
          timestamp: m.created_at,
          is_mine: m.direction === 'outbound'
        }));
        setTeamMessages(twilioMessages);
      } else {
        const res = await api.get(`/messages/conversations/${conv.id}`);
        // Backend returns a flat List[MessageResponse]; normalise + compute is_mine
        const raw = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
        setTeamMessages(raw.map(m => ({
          id: m.id,
          content: m.content,
          sender_id: m.sender_id,
          timestamp: m.created_at || m.timestamp,
          is_mine: m.sender_id === user?.id,
        })));
      }
      setTimeout(scrollToTeamBottom, 100);
    } catch (error) {
      setTeamMessages([]);
    }
  };

  // ---------- Conversation prefs (mute / hide) ----------
  const loadConvPrefs = useCallback(async () => {
    try {
      const res = await api.get('/messages/conversations/prefs');
      const map = {};
      (res.data?.prefs || []).forEach((p) => {
        map[p.conversation_id] = { muted: !!p.muted, hidden: !!p.hidden };
      });
      setConvPrefs(map);
    } catch (e) { /* silent */ }
  }, [api]);

  useEffect(() => {
    if (isOnboarded) loadConvPrefs();
  }, [isOnboarded, loadConvPrefs]);

  const isHidden = (id) => !!convPrefs[id]?.hidden;
  const isMuted = (id) => !!convPrefs[id]?.muted;

  const toggleConvMute = async (id) => {
    try {
      const res = await api.post(`/messages/conversations/${id}/mute`);
      setConvPrefs((p) => ({ ...p, [id]: { ...(p[id] || {}), muted: !!res.data?.muted } }));
    } catch (e) {
      toast.error('Could not change mute');
    }
  };
  const toggleConvHide = async (id) => {
    try {
      const res = await api.post(`/messages/conversations/${id}/hide`);
      setConvPrefs((p) => ({ ...p, [id]: { ...(p[id] || {}), hidden: !!res.data?.hidden } }));
      // Reset the swipe drawer for this row after action
      setSwipeOffsets((s) => ({ ...s, [id]: 0 }));
    } catch (e) {
      toast.error('Could not hide');
    }
  };

  const handleToggleShowHidden = () => {
    setShowHidden((cur) => {
      // turning ON  → start a 60s auto-rehide timer
      // turning OFF → clear it (immediate hide)
      if (showHiddenTimerRef.current) {
        clearTimeout(showHiddenTimerRef.current);
        showHiddenTimerRef.current = null;
      }
      const next = !cur;
      if (next) {
        showHiddenTimerRef.current = setTimeout(() => {
          setShowHidden(false);
          showHiddenTimerRef.current = null;
        }, 60000);
      }
      return next;
    });
  };

  useEffect(() => () => {
    if (showHiddenTimerRef.current) clearTimeout(showHiddenTimerRef.current);
  }, []);

  // ---------- Deep link: /chat?conv=<id> auto-opens the thread ----------
  useEffect(() => {
    const convId = searchParams.get('conv');
    if (!convId) return;
    if (!teamConversations.length && !externalConversations.length) return;
    // Already viewing it — nothing to do
    if (selectedConversation?.id === convId) return;
    // Try team first, then external (twilio).
    const team = teamConversations.find((c) => c.id === convId);
    if (team) {
      setMainTab('internal');
      handleSelectTeamConversation(team);
      // Clear the param so we don't fight subsequent navigation
      const next = new URLSearchParams(searchParams);
      next.delete('conv');
      setSearchParams(next, { replace: true });
      return;
    }
    const ext = externalConversations.find((c) => c.id === convId);
    if (ext) {
      setMainTab('external');
      handleSelectTeamConversation(ext);
      const next = new URLSearchParams(searchParams);
      next.delete('conv');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, teamConversations, externalConversations, selectedConversation?.id, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps


  // the shape the UI expects (with is_mine).
  const normaliseTeamMessages = useCallback((data) => {
    const raw = Array.isArray(data) ? data : (data?.messages || []);
    return raw.map((m) => ({
      id: m.id,
      content: m.content,
      sender_id: m.sender_id,
      timestamp: m.created_at || m.timestamp,
      is_mine: m.sender_id === user?.id,
    }));
  }, [user?.id]);

  const handleSendTeamMessage = async () => {
    if (!newTeamMessage.trim() || !selectedConversation) return;
    const messageContent = newTeamMessage.trim();
    setNewTeamMessage('');

    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      content: messageContent,
      sender_id: user?.id,
      timestamp: new Date().toISOString(),
      is_mine: true,
    };
    setTeamMessages((prev) => [...prev, tempMessage]);
    setTimeout(scrollToTeamBottom, 100);

    // ----- TWILIO / EXTERNAL SMS path -----
    if (selectedConversation._twilio) {
      const isPending = String(selectedConversation.id || '').startsWith('__new_ext_');
      const phone = selectedConversation.phone_number;
      try {
        await api.post('/twilio/send-sms', { to_phone_number: phone, message: messageContent });
        // After the first send the Twilio thread exists — pull it back so the
        // conversation has the real ID for subsequent sends.
        const list = (await api.get('/twilio/contacts/my-external')).data?.contacts || [];
        const target = list.find((c) => (c.phone_number || '').replace(/\D/g, '') === (phone || '').replace(/\D/g, ''));
        if (target) {
          if (isPending) {
            setSelectedConversation({
              id: target.id,
              type: 'external',
              participant_name: target.name || target.phone_number,
              phone_number: target.phone_number,
              _twilio: true,
            });
          }
          const histRes = await api.get(`/twilio/contacts/${target.id}/history`);
          const twilioMessages = (histRes.data?.messages || []).map((m) => ({
            id: m.id,
            content: m.body,
            sender_id: m.direction === 'outbound' ? user?.id : 'external',
            timestamp: m.created_at,
            is_mine: m.direction === 'outbound',
          }));
          setTeamMessages(twilioMessages);
        }
        loadTwilioExternalContacts();
      } catch (error) {
        const detail = error?.response?.data?.detail || 'Failed to send SMS';
        toast.error(detail);
        setTeamMessages((prev) => prev.filter((m) => m.id !== tempId));
        setNewTeamMessage(messageContent);
      }
      return;
    }

    // ----- INTERNAL TEAM CHAT — REST is the source of truth -----
    // (WebSocket receives broadcasts from others; sending via REST is more
    // reliable and gives us a real error to surface.)
    try {
      await api.post(`/messages/conversations/${selectedConversation.id}`, { content: messageContent });
      const res = await api.get(`/messages/conversations/${selectedConversation.id}`);
      setTeamMessages(normaliseTeamMessages(res.data));
      // Refresh the list view so the last_message preview also updates
      loadTeamConversations();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to send message';
      toast.error(detail);
      setTeamMessages((prev) => prev.filter((m) => m.id !== tempId));
      setNewTeamMessage(messageContent);
    }
  };

  const handleStartNewExternalSMS = async () => {
    // Phone source: explicit selectedRecipient (existing contact OR raw-phone tile),
    // then the legacy externalPhoneNumber state, then the search query if it
    // looks like a phone number.
    const isPhoneLike = (s) => /^[\d\s\-\+\(\)]+$/.test(s || '') && (s || '').replace(/\D/g, '').length >= 7;
    const phone = (
      selectedRecipient?.phone ||
      selectedRecipient?.phone_number ||
      externalPhoneNumber ||
      (isPhoneLike(toSearchQuery) ? toSearchQuery : '')
    ).trim();
    if (!phone) {
      toast.error('Enter a phone number or pick a contact');
      return;
    }
    try {
      await api.post('/twilio/send-sms', {
        to_phone_number: phone,
        message: (newTeamMessage || '').trim() || 'Hello'
      });
      setExternalPhoneNumber('');
      setNewTeamMessage('');
      setSelectedRecipient(null);
      setToSearchQuery('');
      await loadTwilioExternalContacts();
      // Open the freshly-created Twilio thread so the user lands inside it
      try {
        const res = await api.get('/twilio/contacts/my-external');
        const list = res.data?.contacts || [];
        const target = list.find(c => (c.phone_number || '').replace(/\D/g, '') === phone.replace(/\D/g, ''));
        if (target) {
          await handleSelectTeamConversation({
            id: target.id,
            type: 'external',
            participant_name: target.name || target.phone_number,
            phone_number: target.phone_number,
            _twilio: true,
          });
          return;
        }
      } catch (e) { /* ignore */ }
      setTeamView('list');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    }
  };

  const handleStartNewTeamConversation = async () => {
    // EXTERNAL — open an empty thread; no SMS is sent until the user types and hits send.
    if (mainTab === 'external') {
      const isPhoneLike = (s) => /^[\d\s\-\+\(\)]+$/.test(s || '') && (s || '').replace(/\D/g, '').length >= 7;
      const phone = (
        selectedRecipient?.phone ||
        selectedRecipient?.phone_number ||
        (isPhoneLike(toSearchQuery) ? toSearchQuery : '')
      ).trim();
      if (!phone) {
        toast.error('Pick an external contact or type a phone number');
        return;
      }
      // First — does a Twilio thread already exist for this number?
      try {
        const list = (await api.get('/twilio/contacts/my-external')).data?.contacts || [];
        const target = list.find((c) => (c.phone_number || '').replace(/\D/g, '') === phone.replace(/\D/g, ''));
        if (target) {
          await handleSelectTeamConversation({
            id: target.id,
            type: 'external',
            participant_name: target.name || target.phone_number,
            phone_number: target.phone_number,
            _twilio: true,
          });
          setSelectedRecipient(null);
          setToSearchQuery('');
          // iter175cl — Prefill applies to existing threads too.
          if (pendingExternalPrefillRef.current) {
            setNewTeamMessage(pendingExternalPrefillRef.current);
            pendingExternalPrefillRef.current = '';
          }
          return;
        }
      } catch (e) { /* fall through to pending thread */ }
      // No existing Twilio thread — create a local "pending" one. The thread is
      // promoted to a real Twilio conversation on the first send.
      setSelectedConversation({
        id: `__new_ext_${phone.replace(/\D/g, '')}`,
        type: 'external',
        participant_name: selectedRecipient?.name || phone,
        phone_number: phone,
        _twilio: true,
      });
      setTeamMessages([]);
      setTeamView('conversation');
      setSelectedRecipient(null);
      setToSearchQuery('');
      // iter175cl — If we entered the composer via the ORB Text
      // deep-link, drop the prefilled body into the chat input now
      // so the user just has to tap Send.
      if (pendingExternalPrefillRef.current) {
        setNewTeamMessage(pendingExternalPrefillRef.current);
        pendingExternalPrefillRef.current = '';
      }
      return;
    }
    // INTERNAL — single OR group chat.
    const ids = selectedRecipients.length > 0
      ? selectedRecipients.map((r) => r.id)
      : (selectedRecipient ? [selectedRecipient.id] : []);
    if (!ids.length) {
      toast.error('Pick at least one teammate');
      return;
    }
    try {
      const res = await api.post('/messages/conversations', {
        type: 'team',
        recipient_ids: ids,
      });
      // Resolve a friendly "participant_name" for the conversation header.
      const namesById = Object.fromEntries(internalUsers.map((u) => [u.id, `${u.first_name} ${u.last_name}`]));
      const participantNames = ids.map((id) => namesById[id]).filter(Boolean);
      const isGroup = ids.length > 1;
      setSelectedConversation({
        ...res.data,
        participant_name: isGroup ? `${participantNames.slice(0,2).join(', ')}${participantNames.length > 2 ? ` +${participantNames.length - 2}` : ''}` : participantNames[0] || 'Team Member',
        is_group: isGroup,
      });
      setTeamView('conversation');
      setTeamMessages([]);
      setSelectedRecipient(null);
      setSelectedRecipients([]);
      setToSearchQuery('');
      await loadTeamConversations();
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const handleTeamKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendTeamMessage();
    }
  };

  // iOS keyboard fix: force scroll reset when keyboard dismisses
  const handleBlur = useCallback(() => {
    setInputFocused(false);
    // Force scroll to top of page to eliminate white space on iOS
    setTimeout(() => {
      // Reset all possible scroll positions
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 50);
    
    // Secondary reset after animation completes
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  }, []);

  // Dismiss keyboard when scrolling/touching the messages area
  const handleMessageAreaTouch = useCallback(() => {
    if (inputFocused && textareaRef.current) {
      textareaRef.current.blur();
    }
  }, [inputFocused]);

  // iOS Safari visual viewport fix - handles keyboard appearance/disappearance
  useEffect(() => {
    const handleResize = () => {
      // When visual viewport changes (keyboard opens/closes), reset scroll
      if (!inputFocused) {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
      }
    };

    // Use visualViewport API if available (iOS Safari)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
    };
  }, [inputFocused]);

  // Save chat state to sessionStorage for persistence when navigating away
  useEffect(() => {
    if (messages.length > 0 || currentSessionId) {
      sessionStorage.setItem(CHAT_STATE_KEY, JSON.stringify({
        messages,
        sessionId: currentSessionId,
        isViewingHistory
      }));
    }
  }, [messages, currentSessionId, isViewingHistory]);

  useEffect(() => {
    loadConversations();
    // Only generate new session if we don't have one restored from sessionStorage
    if (!currentSessionId) {
      const newSessionId = generateSessionId();
      setCurrentSessionId(newSessionId);
      localStorage.setItem(SESSION_KEY, newSessionId);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-resize textarea — handles three cases:
  //   1. Typing (input state changes) — useLayoutEffect below.
  //   2. Textarea re-mounting (user switches Team / AI / External tabs
  //      and comes back to a draft) — callback ref below, since
  //      useLayoutEffect doesn't re-run when only the child DOM node
  //      remounts but the parent state stays the same.
  //   3. Programmatic input set (suggested prompt click) — same path
  //      as (1) via input dep.
  const resizeTextarea = useCallback((node) => {
    if (!node) return;
    node.style.height = 'auto';
    const lineHeight = 20;
    const maxHeight = lineHeight * 8;
    const scrollHeight = node.scrollHeight;
    node.style.height = Math.min(scrollHeight, maxHeight) + 'px';
  }, []);

  const textareaCallbackRef = useCallback((node) => {
    textareaRef.current = node;
    if (node) {
      resizeTextarea(node);
      // Second pass on the next frame — scrollHeight may need an
      // extra layout cycle if the textarea was just mounted and the
      // browser hasn't finished line-breaking the preserved draft.
      requestAnimationFrame(() => resizeTextarea(node));
    }
  }, [resizeTextarea]);

  useLayoutEffect(() => {
    resizeTextarea(textareaRef.current);
  }, [input, resizeTextarea]);

  const loadConversations = async () => {
    try {
      const res = await api.get('/chat/history');
      const history = res.data.history || [];
      const grouped = groupChatHistory(history);
      setConversations(grouped);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const groupChatHistory = (history) => {
    const groups = {};
    history.forEach(item => {
      const sessionId = item.session_id || new Date(item.created_at).toDateString();
      if (!groups[sessionId]) {
        groups[sessionId] = {
          id: sessionId,
          session_id: sessionId,
          messages: [],
          preview: item.user_message?.substring(0, 50) + '...',
          created_at: item.created_at
        };
      }
      groups[sessionId].messages.push(item);
    });
    return Object.values(groups).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const startNewConversation = () => {
    if (messages.length > 0) {
      loadConversations();
    }
    setMessages([]);
    setIsViewingHistory(false);
    const newSessionId = generateSessionId();
    setCurrentSessionId(newSessionId);
    localStorage.setItem(SESSION_KEY, newSessionId);
    setActiveTab('chat');
  };

  const loadConversation = (conversation) => {
    const formattedMessages = [];
    (conversation.messages || []).forEach(item => {
      const msgCharacter = item.character || 'orb';
      formattedMessages.push({
        id: `${item.id}-user`,
        type: 'user',
        content: item.user_message,
        timestamp: item.created_at,
        character: msgCharacter
      });
      formattedMessages.push({
        id: `${item.id}-ai`,
        type: 'ai',
        content: item.ai_response,
        timestamp: item.created_at,
        character: msgCharacter
      });
    });
    setMessages(formattedMessages);
    setIsViewingHistory(true);
    setCurrentSessionId(conversation.session_id);
    setActiveTab('chat');
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: `temp-${Date.now()}-user`,
      type: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
      character: selectedCharacter
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/chat', { 
        message: userMessage.content,
        character: selectedCharacter,
        session_id: currentSessionId
      });
      const aiMessage = {
        id: `temp-${Date.now()}-ai`,
        type: 'ai',
        content: res.data.response,
        timestamp: res.data.timestamp,
        character: selectedCharacter
      };
      setMessages(prev => [...prev, aiMessage]);
      loadConversations();
    } catch (error) {
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      setInput(userMessage.content);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectCharacter = (characterId) => {
    setSelectedCharacter(characterId);
    localStorage.setItem(STORAGE_KEY, characterId);
    setShowCharacterPicker(false);
  };

  const suggestedPrompts = [
    "What is one thing I need to know before I start here?",
    "What am I supposed to be doing as an affiliate?",
    "I've never done affiliate work before, what's it like?",
    "What does Collective Solutions do? I'm confused."
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Stable navigate callback for CharacterAvatar (memoized so React.memo can short-circuit)
  const handleAvatarNavigate = useCallback((id) => navigate(`/character/${id}`), [navigate]);

  // HISTORY PAGE - Using fixed positioning for consistency
  if (activeTab === 'history') {
    return (
      <div className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: bottomOffset }} data-testid="chat-history-page">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setActiveTab('chat'); setMainTab('ai'); }} className="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center" data-testid="back-to-chat">
            <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Chat History</h1>
            <p className="text-xs text-muted-foreground">Your past conversations</p>
          </div>
        </div>

        {conversations.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-muted-foreground">No conversation history yet</p>
            <Button variant="outline" className="mt-4" onClick={() => { setActiveTab('chat'); setMainTab('ai'); }}>Start a conversation</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map((conv, idx) => (
              <div key={conv.id || idx} className="cursor-pointer bg-white/90 dark:bg-[#1a1a1a]/90 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-gray-800" onClick={() => loadConversation(conv)} data-testid={`conversation-${idx}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#586D52] to-[#3d4d38] flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate text-gray-900 dark:text-white">{conv.preview || 'Conversation'}</p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {formatDate(conv.created_at)}
                      <span className="mx-1">•</span>
                      <span>{conv.messages?.length || 0} messages</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Team Conversation View
  if ((mainTab === 'internal' || mainTab === 'external') && teamView === 'conversation' && selectedConversation) {
    const isExternal = mainTab === 'external';
    return (
      <div className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: bottomOffset }} data-testid="team-conversation-page">
        {/* iter123 — Conversation header sits in its own padded card
            with rounded edges (was a flush-edge strip glued to the
            app header). 12px top padding + 16px side gutters + a
            white card with rounded-2xl brings it down and softens
            the corners to match the rest of the app. */}
        <div className="flex-shrink-0 px-3 pt-3">
          <div className="rounded-2xl bg-white/90 dark:bg-[#1a1a1a]/90 shadow-sm border border-black/5 dark:border-white/5 p-3">
            <div className="flex items-center gap-3">
              <button onClick={() => { setTeamView('list'); setSelectedConversation(null); }} className="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <Avatar className="h-10 w-10">
                <AvatarFallback className={isExternal ? 'bg-[#757150]/20 text-[#757150]' : 'bg-[#586D52]/20 text-[#586D52]'}>
                  {selectedConversation.participant_name?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{selectedConversation.participant_name || 'Unknown'}</p>
                </div>
                <p className="text-xs text-muted-foreground">{isExternal ? 'External' : 'Team Member'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-3">
            {teamMessages.map((msg, idx) => (
              <div key={msg.id || idx} className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${msg.is_mine ? (isExternal ? 'bg-[#757150] text-white' : 'bg-[#586D52] text-white') : 'bg-white dark:bg-[#2a2a2a]'}`}>
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${msg.is_mine ? 'text-white/70' : 'text-muted-foreground'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* iter123 — Input rail simplified: no outer card border, no
            top divider — just transparent breathing room above the
            nav so the Input's own border is the only outline. Sits
            16px above the bottom safe area so it isn't hugging the
            edge. */}
        <div
          className="flex-shrink-0 px-3 pt-2 bg-transparent"
          style={{ paddingBottom: inputFocused ? '6px' : 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="flex gap-2">
            <Input
              value={newTeamMessage}
              onChange={(e) => setNewTeamMessage(e.target.value)}
              onKeyPress={handleTeamKeyPress}
              onFocus={handleChatInputFocus}
              onBlur={handleChatInputBlur}
              placeholder="Type a message..."
              className="flex-1 bg-white dark:bg-[#1a1a1a]"
              style={{ fontSize: '16px' }}
              data-testid="chat-input"
            />
            <Button onClick={handleSendTeamMessage} className={isExternal ? 'bg-[#757150] hover:bg-[#8a8560]' : 'bg-[#586D52] hover:bg-[#4a5d46]'} data-testid="team-chat-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // New Team Conversation View
  if ((mainTab === 'internal' || mainTab === 'external') && teamView === 'new-conversation') {
    const isExternal = mainTab === 'external';
    const filteredUsers = isExternal 
      ? externalContacts.filter(c => c.name?.toLowerCase().includes(toSearchQuery.toLowerCase()) || c.phone?.includes(toSearchQuery))
      : internalUsers.filter(u => `${u.first_name} ${u.last_name}`.toLowerCase().includes(toSearchQuery.toLowerCase()));

    return (
      <div className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: bottomOffset }} data-testid="new-conversation-page">
        <div className="flex-shrink-0 p-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => { setTeamView('list'); setSelectedRecipient(null); setSelectedRecipients([]); setToSearchQuery(''); }} className="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold">New {isExternal ? 'External Text' : 'Team Chat'}</h2>
              {!isExternal && (
                <p className="text-[11px] text-muted-foreground">Tap multiple teammates to start a group chat</p>
              )}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={toSearchQuery}
              onChange={(e) => setToSearchQuery(e.target.value)}
              placeholder={isExternal ? 'Search contacts or enter phone...' : 'Search team members...'}
              className="pl-9"
            />
          </div>
          {/* iter175cl — When ORB Text deep-linked us here, surface the
              queued message so the user can confirm what'll be sent
              before they tap Start Conversation. */}
          {isExternal && pendingExternalPrefillRef.current && (
            <div className="mt-3 flex items-start gap-2 p-2.5 rounded-xl bg-[#757150]/10 border border-[#757150]/30" data-testid="orb-text-prefill-banner">
              <Sparkles className="w-4 h-4 text-[#757150] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 text-[11px]">
                <p className="font-semibold text-[#586D52] dark:text-[#a8b8a0]">Message ready to send</p>
                <p className="text-muted-foreground line-clamp-2 mt-0.5">{pendingExternalPrefillRef.current}</p>
              </div>
            </div>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-3 space-y-1">
            {/* Phone-number tile for external when search looks like a number */}
            {isExternal && /^[\d\s\-\+\(\)]+$/.test(toSearchQuery) && toSearchQuery.replace(/\D/g, '').length >= 7 && (
              <div
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors border-2 border-dashed ${selectedRecipient?.phone === toSearchQuery ? 'bg-[#757150]/10 border-[#757150]' : 'border-[#757150]/40 hover:bg-[#757150]/5'}`}
                onClick={() => setSelectedRecipient({ id: `__phone__${toSearchQuery}`, phone: toSearchQuery, name: toSearchQuery })}
                data-testid="external-phone-tile"
              >
                <div className="h-10 w-10 rounded-full bg-[#757150] flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Send to: {toSearchQuery}</p>
                  <p className="text-xs text-muted-foreground">New phone number</p>
                </div>
                {selectedRecipient?.phone === toSearchQuery && <Check className="w-5 h-5 text-[#757150]" />}
              </div>
            )}
            {filteredUsers.map((item) => {
              // INTERNAL = multi-select via selectedRecipients[]; EXTERNAL = single select via selectedRecipient
              const isPickedExt = !isExternal ? false : selectedRecipient?.id === item.id;
              const isPickedInt = !!isExternal ? false : !!selectedRecipients.find((r) => r.id === item.id);
              const isPicked = isExternal ? isPickedExt : isPickedInt;
              const handleRowClick = () => {
                if (isExternal) {
                  setSelectedRecipient(item);
                } else {
                  setSelectedRecipients((prev) => {
                    if (prev.find((r) => r.id === item.id)) {
                      return prev.filter((r) => r.id !== item.id);
                    }
                    return [...prev, item];
                  });
                }
              };
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${isPicked ? (isExternal ? 'bg-[#757150]/10 border border-[#757150]/30' : 'bg-[#586D52]/10 border border-[#586D52]/30') : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  onClick={handleRowClick}
                  data-testid={`recipient-row-${item.id}`}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className={isExternal ? 'bg-[#757150]/20 text-[#757150]' : 'bg-[#586D52]/20 text-[#586D52]'}>
                      {isExternal ? (item.name?.[0] || 'C') : getTeamInitials(item)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{isExternal ? item.name : `${item.first_name} ${item.last_name}`}</p>
                    <p className="text-xs text-muted-foreground">{isExternal ? item.phone : item.email}</p>
                  </div>
                  {isPicked && <Check className={`w-5 h-5 ${isExternal ? 'text-[#757150]' : 'text-[#586D52]'}`} />}
                </div>
              );
            })}
          </div>
        </ScrollArea>

        {/* Multi-select chips for internal team chat */}
        {!isExternal && selectedRecipients.length > 0 && (
          <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-white/60 dark:bg-[#1a1a1a]/60">
            <div className="flex flex-wrap gap-1.5">
              {selectedRecipients.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedRecipients((prev) => prev.filter((p) => p.id !== r.id))}
                  className="flex items-center gap-1 bg-[#586D52]/15 text-[#586D52] text-xs font-medium pl-2 pr-1 py-1 rounded-full"
                  data-testid={`recipient-chip-${r.id}`}
                >
                  {r.first_name} {r.last_name}
                  <XIcon className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Start button — own paddingBottom adds safe-area so the button stays
            above the bottom nav on iPhones with a home indicator. AI Chat is untouched. */}
        <div
          className="flex-shrink-0 px-3 pt-3 border-t border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-[#0d0d0d]"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <Button
            className={`w-full ${isExternal ? 'bg-[#757150] hover:bg-[#8a8560]' : 'bg-[#586D52] hover:bg-[#4a5d46]'}`}
            onClick={handleStartNewTeamConversation}
            disabled={
              isExternal
                ? !selectedRecipient && !(/^[\d\s\-\+\(\)]+$/.test(toSearchQuery) && toSearchQuery.replace(/\D/g, '').length >= 7)
                : selectedRecipients.length === 0
            }
            data-testid="start-conversation-btn"
          >
            {!isExternal && selectedRecipients.length > 1 ? `Start Group Chat (${selectedRecipients.length})` : 'Start Conversation'}
          </Button>
        </div>
      </div>
    );
  }

  // MAIN VIEW with Header and Toggle
  return (
    <div 
      ref={containerRef}
      className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" 
      style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: bottomOffset }}
      data-testid="chat-page"
    >
      {/* Chat Header */}
      <div className="flex-shrink-0 px-4 pt-2 pb-2">
        {/* Three-way Toggle - styled like Calls page */}
        <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-full">
          <button
            onClick={() => { setMainTab('internal'); setTeamView('list'); }}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all flex-1 ${mainTab === 'internal' ? 'bg-[#586D52] text-white shadow' : ''}`}
            data-testid="internal-team-tab"
          >
            <Users className="w-4 h-4 mr-2" />
            Team Chat
          </button>
          {!isRestricted && (
            <button
              onClick={() => setMainTab('ai')}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all flex-1 ${mainTab === 'ai' ? `shadow` : ''}`}
              style={mainTab === 'ai' ? { backgroundColor: currentCharacter.color === '#FFFFFF' ? '#4a5d46' : currentCharacter.color, color: 'white' } : {}}
              data-testid="ai-chat-tab"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Chat
            </button>
          )}
          <button
            onClick={() => { setMainTab('external'); setTeamView('list'); }}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all flex-1 ${mainTab === 'external' ? 'bg-[#757150] text-white shadow' : ''}`}
            data-testid="external-text-tab"
          >
            <Phone className="w-4 h-4 mr-2" />
            External Text
          </button>
        </div>
      </div>

      {/* Internal Team Tab */}
      {mainTab === 'internal' && (
        <div ref={teamChatScrollRef} className="flex-1 overflow-y-auto p-4 pt-2 relative">
          <PullToRefreshIndicator pullY={ptrPullY} refreshing={ptrRefreshing} />
          {/* iter123/iter125 — Search bar + filter button on the
              SAME row as the New button. Funnel swaps the bar for
              Muted + Hidden filter chips. Matches EmailPage flow. */}
          <div className="flex items-center gap-2 mb-3">
            {chatFilterMode === 'search' ? (
              <>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder="Search"
                    className="pl-9 h-9"
                    data-testid="team-list-search"
                  />
                </div>
                <button
                  onClick={() => { setChatFilterMode('filter'); setListSearch(''); }}
                  className="h-9 w-9 rounded-full flex items-center justify-center border border-[#586D52]/30 bg-white dark:bg-[#1a1a1a] text-[#586D52] hover:bg-[#586D52]/10 flex-shrink-0"
                  aria-label="Show filters"
                  data-testid="team-list-filter-toggle"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                {[
                  { key: 'muted', label: 'Muted', on: chatFilterMuted, set: setChatFilterMuted },
                  { key: 'hidden', label: 'Hidden', on: chatFilterHidden, set: setChatFilterHidden },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => f.set((v) => !v)}
                    className={`flex-1 h-9 rounded-full text-xs font-semibold border transition-colors ${f.on ? 'bg-[#586D52] text-white border-[#586D52]' : 'bg-white dark:bg-[#1a1a1a] text-[#586D52] border-[#586D52]/30 hover:bg-[#586D52]/10'}`}
                    data-testid={`team-list-filter-${f.key}`}
                  >{f.label}</button>
                ))}
                <button
                  onClick={() => { setChatFilterMode('search'); setChatFilterMuted(false); setChatFilterHidden(false); }}
                  className="h-9 w-9 rounded-full flex items-center justify-center border border-[#586D52]/30 bg-white dark:bg-[#1a1a1a] text-[#586D52] hover:bg-[#586D52]/10 flex-shrink-0"
                  aria-label="Back to search"
                  data-testid="team-list-filter-close"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </>
            )}
            <Button size="sm" className="bg-[#586D52] hover:bg-[#4a5d46] flex-shrink-0" onClick={() => setTeamView('new-conversation')}>
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </div>
          
          {!isOnboarded ? (
            <div className="text-center py-12 bg-white/80 dark:bg-[#1a1a1a]/80 rounded-2xl">
              <Lock className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground">Complete onboarding to access team chat</p>
            </div>
          ) : teamLoading ? (
            <PageLoader label="Loading conversations..." fullScreen={false} gradient={false} testId="team-list-loading" />
          ) : teamConversations.length === 0 ? (
            <div className="text-center py-12 bg-white/80 dark:bg-[#1a1a1a]/80 rounded-2xl">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-3">No team conversations yet</p>
              <Button variant="outline" onClick={() => setTeamView('new-conversation')}>Start a conversation</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {teamConversations
                .filter((c) => {
                  // iter125 — Hidden behaviour now driven by chip:
                  // off → strip hidden out of list; on → ONLY hidden.
                  const hidden = isHidden(c.id);
                  if (chatFilterHidden) return hidden;
                  return !hidden;
                })
                .filter((c) => !chatFilterMuted || isMuted(c.id))
                .filter((c) => {
                  const q = listSearch.trim().toLowerCase();
                  if (!q) return true;
                  return `${c.participant_name || ''} ${c.last_message || ''}`.toLowerCase().includes(q);
                })
                .map((conv) => (
                <SwipeableConvRow
                  key={conv.id}
                  id={conv.id}
                  isMuted={isMuted(conv.id)}
                  hidden={isHidden(conv.id)}
                  offset={swipeOffsets[conv.id] || 0}
                  setOffset={(rid, off) => setSwipeOffsets((s) => ({ ...s, [rid]: off }))}
                  onMute={() => toggleConvMute(conv.id)}
                  onHide={() => toggleConvHide(conv.id)}
                  onClick={() => handleSelectTeamConversation(conv)}
                  palette="green"
                >
                  {/* iter125.3 — Profile image + name + title row.
                      Mirrors the email thread card three-line layout. */}
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 mt-0.5 flex-shrink-0">
                      {conv.participant_picture ? <AvatarImage src={conv.participant_picture} alt="" /> : null}
                      <AvatarFallback className="bg-[#586D52]/20 text-[#586D52] font-semibold text-sm">{conv.participant_name?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold truncate flex-1 flex items-center gap-1">
                          {conv.participant_name}
                          {isMuted(conv.id) && <BellOff className="w-3 h-3 text-muted-foreground" />}
                        </p>
                        <span className="text-[11px] text-muted-foreground flex-shrink-0">{conv.last_message_at ? new Date(conv.last_message_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}</span>
                      </div>
                      <p className="text-sm truncate text-foreground/90 mt-0.5">{conv.last_message || 'No messages yet'}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.participant_title || 'Team Member'}</p>
                    </div>
                  </div>
                </SwipeableConvRow>
              ))}
            </div>
          )}
        </div>
      )}

      {/* External Text Tab */}
      {mainTab === 'external' && (
        <div className="flex-1 overflow-y-auto p-4 pt-2">
          {/* iter123/iter125 — Same search+filter pattern as Team Chat. */}
          <div className="flex items-center gap-2 mb-3">
            {chatFilterMode === 'search' ? (
              <>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder="Search"
                    className="pl-9 h-9"
                    data-testid="external-list-search"
                  />
                </div>
                <button
                  onClick={() => { setChatFilterMode('filter'); setListSearch(''); }}
                  className="h-9 w-9 rounded-full flex items-center justify-center border border-[#757150]/30 bg-white dark:bg-[#1a1a1a] text-[#757150] hover:bg-[#757150]/10 flex-shrink-0"
                  aria-label="Show filters"
                  data-testid="external-list-filter-toggle"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                {[
                  { key: 'muted', label: 'Muted', on: chatFilterMuted, set: setChatFilterMuted },
                  { key: 'hidden', label: 'Hidden', on: chatFilterHidden, set: setChatFilterHidden },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => f.set((v) => !v)}
                    className={`flex-1 h-9 rounded-full text-xs font-semibold border transition-colors ${f.on ? 'bg-[#757150] text-white border-[#757150]' : 'bg-white dark:bg-[#1a1a1a] text-[#757150] border-[#757150]/30 hover:bg-[#757150]/10'}`}
                    data-testid={`external-list-filter-${f.key}`}
                  >{f.label}</button>
                ))}
                <button
                  onClick={() => { setChatFilterMode('search'); setChatFilterMuted(false); setChatFilterHidden(false); }}
                  className="h-9 w-9 rounded-full flex items-center justify-center border border-[#757150]/30 bg-white dark:bg-[#1a1a1a] text-[#757150] hover:bg-[#757150]/10 flex-shrink-0"
                  aria-label="Back to search"
                  data-testid="external-list-filter-close"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </>
            )}
            <Button size="sm" className="bg-[#757150] hover:bg-[#8a8560] flex-shrink-0" onClick={() => setTeamView('new-conversation')}>
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </div>
          
          {!isOnboarded ? (
            <div className="text-center py-12 bg-white/80 dark:bg-[#1a1a1a]/80 rounded-2xl">
              <Lock className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground">Complete onboarding to access external texting</p>
            </div>
          ) : teamLoading ? (
            <PageLoader label="Loading conversations..." fullScreen={false} gradient={false} testId="external-list-loading" />
          ) : externalConversations.length === 0 ? (
            <div className="text-center py-12 bg-white/80 dark:bg-[#1a1a1a]/80 rounded-2xl">
              <Phone className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-3">No external conversations yet</p>
              <Button variant="outline" onClick={() => setTeamView('new-conversation')}>Start a conversation</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {externalConversations
                .filter((c) => {
                  const hidden = isHidden(c.id);
                  if (chatFilterHidden) return hidden;
                  return !hidden;
                })
                .filter((c) => !chatFilterMuted || isMuted(c.id))
                .filter((c) => {
                  const q = listSearch.trim().toLowerCase();
                  if (!q) return true;
                  return `${c.participant_name || ''} ${c.last_message || ''} ${c.phone_number || ''}`.toLowerCase().includes(q);
                })
                .map((conv) => (
                <SwipeableConvRow
                  key={conv.id}
                  id={conv.id}
                  isMuted={isMuted(conv.id)}
                  hidden={isHidden(conv.id)}
                  offset={swipeOffsets[conv.id] || 0}
                  setOffset={(rid, off) => setSwipeOffsets((s) => ({ ...s, [rid]: off }))}
                  onMute={() => toggleConvMute(conv.id)}
                  onHide={() => toggleConvHide(conv.id)}
                  onClick={() => handleSelectTeamConversation(conv)}
                  palette="gold"
                >
                  {/* iter125.3 — Profile image + name + company,title. */}
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 mt-0.5 flex-shrink-0">
                      {conv.participant_picture ? <AvatarImage src={conv.participant_picture} alt="" /> : null}
                      <AvatarFallback className="bg-[#757150]/20 text-[#757150] font-semibold text-sm">{conv.participant_name?.[0] || 'C'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold truncate flex-1 flex items-center gap-1">
                          {conv.participant_name}
                          {isMuted(conv.id) && <BellOff className="w-3 h-3 text-muted-foreground" />}
                        </p>
                        <span className="text-[11px] text-muted-foreground flex-shrink-0">{conv.last_message_at ? new Date(conv.last_message_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}</span>
                      </div>
                      <p className="text-sm truncate text-foreground/90 mt-0.5">{conv.last_message || 'No messages yet'}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.participant_title || conv.phone_number || 'External'}</p>
                    </div>
                  </div>
                </SwipeableConvRow>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AI Chat Tab */}
      {mainTab === 'ai' && (
        <>
          {/* Character Header */}
          <div className="flex-shrink-0 px-4 pb-2">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl px-3 py-2.5 shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <CharacterAvatar character={currentCharacter} clickable={true} onNavigate={handleAvatarNavigate} />
                  <div className="min-w-0 flex-1">
                    <h1 className="text-base font-bold truncate" style={{ color: currentCharacter.color }}>{currentCharacter.name}</h1>
                    <p className="text-xs text-muted-foreground truncate">{currentCharacter.shortDesc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isViewingHistory && messages.length > 0 && (
                    <button onClick={startNewConversation} className="w-7 h-7 rounded-full bg-[#586D52] flex items-center justify-center hover:bg-[#4a5d46] transition-colors shadow-sm" data-testid="new-chat-btn">
                      <Plus className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}
                  <button onClick={() => setActiveTab('history')} className="w-7 h-7 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center hover:bg-gray-200 dark:hover:bg-[#3a3a3a] transition-colors" data-testid="history-btn">
                    <History className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <button onClick={() => setShowCharacterPicker(true)} className="w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm hover:shadow-md" style={{ backgroundColor: currentCharacter.color }} data-testid="character-picker-btn">
                    <Users className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Chat Content */}
          <div 
            className="flex-1 overflow-y-auto px-4" 
            ref={scrollRef}
            onTouchMove={handleMessageAreaTouch}
            onMouseDown={handleMessageAreaTouch}
          >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center px-2 py-4 h-full">
            <CharacterAvatar character={currentCharacter} size="lg" />
            <h2 className="text-lg font-bold mt-3 mb-1" style={{ color: currentCharacter.color }}>{currentCharacter.name}</h2>
            <p className="text-muted-foreground text-xs mb-3 max-w-xs leading-relaxed">{currentCharacter.longDesc}</p>
            <div className="grid grid-cols-1 gap-1.5 w-full max-w-sm">
              {suggestedPrompts.map((prompt, idx) => (
                <button key={idx} className="text-left text-xs p-2 rounded-xl bg-white/80 dark:bg-[#1a1a1a]/80 border border-gray-200 dark:border-gray-700 hover:border-[#586D52] dark:hover:border-[#586D52] transition-colors" onClick={() => setInput(prompt)} data-testid={`suggested-prompt-${idx}`}>
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {messages.map((message) => {
              const msgCharacter = getMessageCharacter(message);
              return (
                <div key={message.id} className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${message.type}`}>
                  {message.type === 'ai' && <div className="flex-shrink-0"><CharacterAvatar character={msgCharacter} size="sm" clickable={true} onNavigate={handleAvatarNavigate} /></div>}
                  <div
                    className={`max-w-[80%] p-3 overflow-hidden ${message.type === 'user' ? 'rounded-2xl rounded-br-md' : 'bg-white dark:bg-[#2a2a2a] rounded-2xl rounded-bl-md shadow-sm border border-gray-100 dark:border-gray-700'}`}
                    style={message.type === 'user' ? { backgroundColor: currentCharacter.color } : undefined}
                  >
                    <p className={`text-sm whitespace-pre-wrap break-words leading-relaxed ${message.type === 'user' ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>{message.content}</p>
                  </div>
                  {message.type === 'user' && <div className="w-7 h-7 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0"><User className="w-3 h-3 text-gray-600 dark:text-gray-300" /></div>}
                </div>
              );
            })}
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0"><CharacterAvatar character={currentCharacter} size="sm" /></div>
                <div className="bg-white dark:bg-[#1a1a1a] p-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100 dark:border-gray-800">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input Area - Flex shrink-0 stays at bottom (sits just above the nav) */}
      <div
        className="flex-shrink-0 px-3 pt-2"
        style={{ paddingBottom: inputFocused ? '6px' : 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex gap-2 max-w-2xl mx-auto items-end">
          <textarea
            ref={textareaCallbackRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={(e) => { setInputFocused(true); handleChatInputFocus?.(e); }}
            onBlur={(e) => { setInputFocused(false); handleChatInputBlur?.(e); }}
            placeholder={`Message ${currentCharacter.name}...`}
            disabled={loading}
            rows={1}
            // iter125 — `scrollbar-hide` + matching webkit override
            // kills the desktop vertical scrollbar that was showing
            // up as a thin line on the far-right of the input box.
            className="flex-1 bg-white/90 dark:bg-[#2a2a2a] border-2 rounded-2xl px-4 py-2.5 text-base resize-none focus:outline-none transition-colors [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
            style={{
              fontSize: '16px',
              minHeight: '40px',
              maxHeight: '160px',
              lineHeight: '20px',
              borderColor: `${currentCharacter.color}55`,
              boxShadow: inputFocused ? `0 0 0 2px ${currentCharacter.color}40` : 'none',
            }}
            data-testid="chat-input"
          />
          <Button
            onTouchStart={(e) => { if (input.trim()) { e.preventDefault(); handleSend(); }}}
            onMouseDown={(e) => { if (input.trim()) { e.preventDefault(); handleSend(); }}}
            disabled={loading || !input.trim()}
            size="icon"
            className="h-10 w-10 rounded-full shadow-md flex-shrink-0 hover:opacity-90"
            style={{ backgroundColor: currentCharacter.color, color: 'white' }}
            data-testid="chat-send-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Character Picker Dialog - Centered */}
      <Dialog open={showCharacterPicker} onOpenChange={setShowCharacterPicker}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Choose AI Personality</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Select a character to chat with</p>
          </DialogHeader>
          <div className="px-3 pb-4 space-y-1.5 max-h-[55vh] overflow-y-auto">
            {Object.values(AI_CHARACTERS).map((character) => (
              <button key={character.id} onClick={() => handleSelectCharacter(character.id)} className={`w-full p-2 rounded-xl text-left transition-all ${selectedCharacter === character.id ? 'bg-gray-100 dark:bg-[#2a2a2a] ring-2' : 'hover:bg-gray-50 dark:hover:bg-[#252525]'}`} style={{ ringColor: selectedCharacter === character.id ? character.color : 'transparent' }} data-testid={`character-${character.id}`}>
                <div className="flex gap-2 items-center">
                  <CharacterAvatar character={character} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-semibold text-sm" style={{ color: character.color }}>{character.name}</span>
                      {selectedCharacter === character.id && <Check className="w-3 h-3" style={{ color: character.color }} />}
                    </div>
                    <p className="text-[10px] text-muted-foreground">{character.shortDesc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
};

export default ChatPage;
;
